import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { useUserDetailsContext } from "./userDetailsContext";
import { TwilioService } from "../views/Chat/services";
import { useConversationsManager } from "../views/Chat/hooks/useConversationsManager";
import { useUnreadStore } from "../views/Chat/store/useOrderStore";
import { ChatType } from "../types/chatTypes";

interface ChatContextType {
  client: any | null;
  identity: string | null;
  isReady: boolean;
  error: string | null;
  isConnected: boolean;
  getUnreadCountByUniqueName: (uniqueName: string) => number;
  clearUnreadCountByUniqueName: (uniqueName: string) => void;
  initializeConversations: () => Promise<{
    conversations: any[];
    cleanup: () => void;
  }>;
  openSupplierChatUniqueName: string | null;
  setOpenSupplierChatUniqueName: (uniqueName: string | null) => void;
  openSupplierChatData: {
    chatType: ChatType.SUPPLIER;
    orderNumber: string;
    participantName: string;
    orderHandlerUserId: string;
    participantId: string;
    uniqueName: string;
  } | null;
  setOpenSupplierChatData: (
    data: {
      chatType: ChatType.SUPPLIER;
      orderNumber: string;
      participantName: string;
      orderHandlerUserId: string;
      participantId: string;
      uniqueName: string;
    } | null
  ) => void;
  openCustomerChatUniqueName: string | null;
  setOpenCustomerChatUniqueName: (uniqueName: string | null) => void;
  openCustomerChatData: {
    chatType: ChatType.CUSTOMER;
    orderNumber: string;
    participantName: string;
    orderHandlerUserId: string;
    participantId: string;
    uniqueName: string;
  } | null;
  setOpenCustomerChatData: (
    data: {
      chatType: ChatType.CUSTOMER;
      orderNumber: string;
      participantName: string;
      orderHandlerUserId: string;
      participantId: string;
      uniqueName: string;
    } | null
  ) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

interface ChatProviderProps {
  children: React.ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const userDetails = useUserDetailsContext();
  const [client, setClient] = useState<any>(null);
  const [identity, setIdentity] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openSupplierChatUniqueName, setOpenSupplierChatUniqueName] = useState<
    string | null
  >(null);
  const [openSupplierChatData, setOpenSupplierChatData] = useState<{
    chatType: ChatType.SUPPLIER;
    orderNumber: string;
    participantName: string;
    orderHandlerUserId: string;
    participantId: string;
    uniqueName: string;
  } | null>(null);

  const [openCustomerChatUniqueName, setOpenCustomerChatUniqueName] = useState<
    string | null
  >(null);
  const [openCustomerChatData, setOpenCustomerChatData] = useState<{
    chatType: ChatType.CUSTOMER;
    orderNumber: string;
    participantName: string;
    orderHandlerUserId: string;
    participantId: string;
    uniqueName: string;
  } | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const initializingRef = useRef(false);
  const destroyedRef = useRef(false);
  const conversationsInitializedRef = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  const initializeClient = useCallback(async () => {
    if (!userDetails?._id || initializingRef.current || destroyedRef.current) {
      return;
    }
    initializingRef.current = true;
    setError(null);

    try {
      const { client: twilioClient, identity: clientIdentity } =
        await TwilioService.createConversationsClient(userDetails._id);

      if (destroyedRef.current) {
        return;
      }

      setClient(twilioClient);
      setIdentity(clientIdentity);
      setIsReady(true);

      const currentState = twilioClient.connectionState;
      setIsConnected(currentState === "connected");

      twilioClient.on("connectionStateChanged", (state: string) => {
        setIsConnected(state === "connected");
      });
    } catch (err: any) {
      setError(err.message || "Failed to initialize chat");
    } finally {
      initializingRef.current = false;
    }
  }, [userDetails?._id]);

  useEffect(() => {
    destroyedRef.current = false;
    if (userDetails?._id) {
      initializeClient();
    }

    return () => {
      destroyedRef.current = true;
      if (client) {
        client.shutdown();
      }
    };
  }, [userDetails?._id, initializeClient]);

  const bus = useConversationsManager({
    client,
    identity,
    selfUserId: userDetails?._id || "",
    openChatUniqueName:
      openSupplierChatUniqueName || openCustomerChatUniqueName,
  });

  useEffect(() => {
    if (
      isReady &&
      isConnected &&
      bus.initializeConversations &&
      !conversationsInitializedRef.current
    ) {
      conversationsInitializedRef.current = true;

      bus
        .initializeConversations()
        .then(({ conversations, cleanup }) => {
          useUnreadStore.getState().setConversations(conversations);

          cleanupRef.current = cleanup;
        })
        .catch((error) => {
          console.error("Failed to initialize conversations:", error);
          conversationsInitializedRef.current = false;
        });
    }

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
        conversationsInitializedRef.current = false;
      }
    };
  }, [isReady, isConnected]);

  const { getUnreadCountByUniqueName, clearUnreadCountByUniqueName } =
    useUnreadStore();

  const value: ChatContextType = {
    client,
    identity,
    isReady,
    error,
    isConnected,
    getUnreadCountByUniqueName,
    clearUnreadCountByUniqueName,
    initializeConversations: bus.initializeConversations,
    openSupplierChatUniqueName,
    setOpenSupplierChatUniqueName,
    openSupplierChatData,
    setOpenSupplierChatData,
    openCustomerChatUniqueName,
    setOpenCustomerChatUniqueName,
    openCustomerChatData,
    setOpenCustomerChatData,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
};
